import { ReactNode } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useCurriculumProgress, ModuleGate } from "@/hooks/useCurriculumProgress";
import { useAdminRole } from "@/hooks/useAdminRole";
import { useAccessTier } from "@/hooks/useAccessTier";
import { isApprovedLaunchModuleKey } from "@/hooks/useLaunchModules";
import { Lock, BookOpen, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

interface SimulatorGateProps {
  moduleKey: string;
  children: ReactNode;
}

/**
 * Wraps a simulator page and shows a lock screen when prerequisites aren't met.
 * Uses useCurriculumProgress for centralized gating logic.
 */
const SimulatorGate = ({ moduleKey, children }: SimulatorGateProps) => {
  const navigate = useNavigate();
  const { moduleGates, courseProgress, loading } = useCurriculumProgress();
  const { isAdmin, loading: adminLoading } = useAdminRole();
  const { tier, loading: tierLoading } = useAccessTier();

  if (loading || adminLoading || tierLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center gap-3">
          <div className="w-5 h-5 rounded-full border-2 border-primary border-t-transparent animate-spin" />
          <p className="text-muted-foreground font-heading">Checking access...</p>
        </div>
      </div>
    );
  }

  // Admin always passes.
  if (isAdmin) return <>{children}</>;

  const gate = moduleGates.find(m => m.moduleKey === moduleKey);
  if (!gate) return <>{children}</>;

  if (gate.isUnlocked) return <>{children}</>;

  // Calculate aggregate progress across missing prerequisites
  const missingCourses = gate.missingPrereqs;
  let totalLessons = 0;
  let completedLessons = 0;
  missingCourses.forEach(courseName => {
    const course = courseProgress.find(c => c.courseName === courseName);
    if (course) {
      totalLessons += course.totalLessons;
      completedLessons += course.completedLessons;
    }
  });
  const pct = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;

  // Find first incomplete prerequisite course for "Go to Course" button
  const firstMissing = missingCourses[0];

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="glass-card glow-border max-w-md w-full p-8 text-center space-y-6">
        <div className="w-16 h-16 rounded-2xl bg-muted/60 flex items-center justify-center mx-auto border border-border/50">
          <Lock className="w-8 h-8 text-muted-foreground" />
        </div>
        <h1 className="font-heading text-xl font-bold text-foreground">{gate.moduleName} — Locked</h1>
        <p className="text-sm text-muted-foreground leading-relaxed">
          {missingCourses.length === 1
            ? `Complete the ${missingCourses[0]} course to unlock this module.`
            : `Complete the following courses to unlock: ${missingCourses.join(", ")}.`}
        </p>
        {totalLessons > 0 && (
          <>
            <Progress value={pct} className="h-2" />
            <p className="text-xs text-muted-foreground font-mono">{completedLessons} / {totalLessons} lessons completed</p>
          </>
        )}
        <div className="flex gap-3 justify-center pt-2">
          <Button variant="outline" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          {firstMissing && (
            <Button onClick={() => navigate(`/course/${encodeURIComponent(firstMissing)}`)}>
              <BookOpen className="w-4 h-4 mr-2" />
              Go to Course
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SimulatorGate;
